/**
 * ShareholderTable, ShareholderRow, SecurityTable for the structure map.
 */

import { useState } from "react";
import { HelpCircle } from "lucide-react";
import { cn } from "@/utils/misc";
import { Badge } from "@/ui/primitives/badge";
import type { EntityItem } from "../types";
import { TYPE_ICON, STRUCTURE_MAP_TYPE_COLORS } from "../constants";
import { SourceDocumentLinks } from "./SectionHeader";

export function ShareholderTable({
  entities,
  onDocumentClick,
}: {
  entities: EntityItem[];
  onDocumentClick?: (documentId: string) => void;
}) {
  const [showAll, setShowAll] = useState(entities.length <= 12);
  const visible = showAll ? entities : entities.slice(0, 12);
  const hiddenCount = entities.length - 12;
  const hasAnySources = entities.some((e) => e.sourceDocuments?.length);

  return (
    <div>
      <div className="overflow-x-auto rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">
                Shareholder
              </th>
              <th className="w-24 px-3 py-2 text-left text-xs font-semibold text-muted-foreground">
                Type
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">
                Details
              </th>
              <th className="w-20 px-3 py-2 text-left text-xs font-semibold text-muted-foreground">
                Status
              </th>
              {hasAnySources && (
                <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">
                  Sources
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {visible.map((entity, i) => (
              <ShareholderRow
                key={entity.name + i}
                entity={entity}
                odd={i % 2 === 1}
                showSources={hasAnySources}
                onDocumentClick={onDocumentClick}
              />
            ))}
          </tbody>
        </table>
      </div>
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          className="mt-2 cursor-pointer text-[11px] text-primary hover:underline"
        >
          {showAll ? "Show fewer" : `+ ${hiddenCount} more...`}
        </button>
      )}
    </div>
  );
}

function ShareholderRow({
  entity,
  odd,
  showSources,
  onDocumentClick,
}: {
  entity: EntityItem;
  odd: boolean;
  showSources: boolean;
  onDocumentClick?: (documentId: string) => void;
}) {
  const Icon = TYPE_ICON[entity.type] ?? HelpCircle;
  const styles =
    STRUCTURE_MAP_TYPE_COLORS[entity.type] ?? STRUCTURE_MAP_TYPE_COLORS.company;

  return (
    <tr className={cn("border-b last:border-0", odd && "bg-muted/15")}>
      <td className="px-3 py-2.5 align-top">
        <div className="flex items-start gap-2">
          <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", styles.text)} />
          <div>
            <p className="text-sm font-medium">{entity.name}</p>
            {(entity.acn || entity.abn) && (
              <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                {entity.acn && `ACN ${entity.acn}`}
                {entity.acn && entity.abn && " · "}
                {entity.abn && `ABN ${entity.abn}`}
              </p>
            )}
          </div>
        </div>
      </td>
      <td className="px-3 py-2.5 align-top">
        <Badge
          variant="outline"
          className={cn("text-[10px] capitalize", styles.text)}
        >
          {entity.type}
        </Badge>
      </td>
      <td className="px-3 py-2.5 align-top text-xs leading-relaxed text-muted-foreground">
        {entity.detail}
      </td>
      <td className="px-3 py-2.5 align-top">
        <Badge
          color={entity.isCurrent === false ? "amber" : "green"}
          variant="soft"
          className="text-[10px]"
        >
          {entity.isCurrent === false ? "Former" : "Current"}
        </Badge>
      </td>
      {showSources && (
        <td className="px-3 py-2.5 align-top">
          <SourceDocumentLinks
            sourceDocuments={entity.sourceDocuments}
            onDocumentClick={onDocumentClick}
          />
        </td>
      )}
    </tr>
  );
}

export function SecurityTable({
  entities,
  onDocumentClick,
}: {
  entities: EntityItem[];
  onDocumentClick?: (documentId: string) => void;
}) {
  const hasAnySources = entities.some((e) => e.sourceDocuments?.length);

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30">
            <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">
              Secured Party
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">
              Security Details
            </th>
            {hasAnySources && (
              <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">
                Sources
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {entities.map((entity, i) => {
            const Icon = TYPE_ICON[entity.type] ?? HelpCircle;
            const styles =
              STRUCTURE_MAP_TYPE_COLORS[entity.type] ??
              STRUCTURE_MAP_TYPE_COLORS.company;
            return (
              <tr
                key={entity.name + i}
                className={cn(
                  "border-b last:border-0",
                  i % 2 === 1 && "bg-muted/15",
                )}
              >
                <td className="px-3 py-2.5 align-top">
                  <div className="flex items-start gap-2">
                    <Icon
                      className={cn("mt-0.5 h-4 w-4 shrink-0", styles.text)}
                    />
                    <div>
                      <p className="text-sm font-medium">{entity.name}</p>
                      {(entity.acn || entity.abn) && (
                        <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                          {entity.acn && `ACN ${entity.acn}`}
                          {entity.acn && entity.abn && " · "}
                          {entity.abn && `ABN ${entity.abn}`}
                        </p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2.5 align-top text-xs leading-relaxed text-muted-foreground">
                  {entity.detail}
                </td>
                {hasAnySources && (
                  <td className="px-3 py-2.5 align-top">
                    <SourceDocumentLinks
                      sourceDocuments={entity.sourceDocuments}
                      onDocumentClick={onDocumentClick}
                    />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
